import type { IPagedResponse, OrderByParam, PaginationParam } from '@/features/api/extranet'
import { usePreferencesUtilisateur } from '@/features/application/preferences'
import {
  PREFERENCES_UTILISATEUR_APP,
  PREFERENCES_UTILISATEUR_APP_PAGESIZE,
  type PreferenceGlobale,
} from '@/features/application/types'
import { loadRecherche, printRecherche } from '@/features/services/recherche'
import { defineStore } from 'pinia'
import { shallowRef } from 'vue'
import type { RouteParamsGeneric, RouteRecordNameGeneric } from 'vue-router'

export type PagedRequest<T extends object> = {
  query: T
  orderBy: OrderByParam
  pagination: PaginationParam
  route?: {
    name: RouteRecordNameGeneric
    params: RouteParamsGeneric
  }
}

export type BuilRechercheOptions = {
  storeName: string
  apiPath: string
}

export function buildRechercheStore<TRequest extends object, TResponse extends object>(
  builderOptions: BuilRechercheOptions,
) {
  return defineStore(builderOptions.storeName, () => {
    const pageData = shallowRef<IPagedResponse<TResponse> | null>(null)
    const pageRequest = shallowRef<PagedRequest<TRequest> | null>(null)

    function $reset() {
      pageData.value = null
      pageRequest.value = null
    }

    async function loadData(request: PagedRequest<TRequest>) {
      if (!request.pagination.pageSize) {
        const prefService = usePreferencesUtilisateur<PreferenceGlobale>(PREFERENCES_UTILISATEUR_APP)
        const userPref = await prefService.getPreferencesAsync()
        request.pagination.pageSize = userPref?.pageSize ?? PREFERENCES_UTILISATEUR_APP_PAGESIZE
      }
      const result = await loadRecherche<TRequest, TResponse>(builderOptions.apiPath, request)

      // Modifie le state si pas d'erreur
      pageRequest.value = request
      pageData.value = result
    }

    async function reloadData() {
      const result = await loadRecherche<TRequest, TResponse>(builderOptions.apiPath, pageRequest.value!)

      // Modifie le state si pas d'erreur
      pageData.value = result
    }

    async function printData() {
      await printRecherche<TRequest>(builderOptions.apiPath, pageRequest.value!)
    }

    return {
      $reset,
      loadData,
      reloadData,
      printData,
      pageRequest,
      pageData,
    }
  })
}
